import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Building2,
  DollarSign,
  CreditCard,
  FileText,
  TrendingUp,
  CheckCircle2,
  Clock,
  AlertCircle,
  Users,
  Wallet,
  Receipt,
  Download,
  Search,
  Loader2,
  RefreshCw,
  BadgeCheck,
  BadgeDollarSign,
  Calendar,
  HandCoins,
  ChevronRight,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PlatformOwnerPaymentDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [dashboardData, setDashboardData] = useState(null);
  const [commissions, setCommissions] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Dialog states
  const [showMarkPaidDialog, setShowMarkPaidDialog] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [paymentReference, setPaymentReference] = useState('');
  const [paymentDate, setPaymentDate] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  // Commission payout dialog
  const [showPayoutDialog, setShowPayoutDialog] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [payoutAmount, setPayoutAmount] = useState('');
  const [payoutPeriodStart, setPayoutPeriodStart] = useState('');
  const [payoutPeriodEnd, setPayoutPeriodEnd] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [dashboardRes, commissionsRes] = await Promise.all([
        fetch(`${API_URL}/api/building-payments/platform/dashboard`, { credentials: 'include' }),
        fetch(`${API_URL}/api/building-payments/platform/commissions`, { credentials: 'include' })
      ]);

      if (dashboardRes.ok) {
        setDashboardData(await dashboardRes.json());
      }
      
      if (commissionsRes.ok) {
        setCommissions(await commissionsRes.json());
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkPaid = async () => {
    if (!selectedInvoice || !paymentReference) {
      toast.error('Please enter payment reference');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/building-payments/invoice/mark-paid`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          invoice_id: selectedInvoice.invoice_id,
          payment_reference: paymentReference,
          payment_date: paymentDate || null
        })
      });

      if (response.ok) {
        toast.success('Invoice marked as paid, buildings activated');
        setShowMarkPaidDialog(false);
        setSelectedInvoice(null);
        setPaymentReference('');
        setPaymentDate('');
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to mark invoice as paid');
      }
    } catch (error) {
      toast.error('Failed to mark invoice as paid');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCommissionPayout = async () => {
    if (!selectedPartner || !payoutAmount || !payoutPeriodStart || !payoutPeriodEnd) {
      toast.error('Please fill all fields');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/building-payments/platform/commission/pay`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          partner_id: selectedPartner.partner_code,
          amount: parseFloat(payoutAmount),
          period_start: payoutPeriodStart,
          period_end: payoutPeriodEnd
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        setShowPayoutDialog(false);
        setSelectedPartner(null);
        setPayoutAmount('');
        setPayoutPeriodStart('');
        setPayoutPeriodEnd('');
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to process payout');
      }
    } catch (error) {
      toast.error('Failed to process payout');
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (amount) => {
    return `R${(amount || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: <Badge className="bg-amber-100 text-amber-700"><Clock className="w-3 h-3 mr-1" />Pending</Badge>,
      pending_payment: <Badge className="bg-amber-100 text-amber-700"><Clock className="w-3 h-3 mr-1" />Pending</Badge>,
      paid: <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle2 className="w-3 h-3 mr-1" />Paid</Badge>,
      payable: <Badge className="bg-blue-100 text-blue-700"><DollarSign className="w-3 h-3 mr-1" />Payable</Badge>,
      failed: <Badge className="bg-red-100 text-red-700"><AlertCircle className="w-3 h-3 mr-1" />Failed</Badge>,
    };
    return badges[status] || <Badge variant="secondary">{status}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  const summary = dashboardData?.summary || {};
  const counts = dashboardData?.counts || {};
  const recentOrders = dashboardData?.recent_transactions?.orders || [];
  const recentInvoices = dashboardData?.recent_transactions?.invoices || [];
  const monthlyData = dashboardData?.monthly_data || [];
  const partnerCommissions = commissions?.by_partner || [];
  const commissionTotals = commissions?.totals || {};

  return (
    <div className="min-h-screen bg-slate-50 p-4 lg:p-8" data-testid="platform-payment-dashboard">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Button variant="ghost" className="mb-2 -ml-2" onClick={() => navigate('/platform')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Platform Dashboard
          </Button>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-slate-900 flex items-center gap-3">
                <Wallet className="w-8 h-8 text-indigo-600" />
                Payment Management
              </h1>
              <p className="text-slate-500 mt-1">Track building payments, invoices, and partner commissions</p>
            </div>
            <Button onClick={fetchData} variant="outline">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Revenue</p>
                  <p className="text-2xl font-bold text-emerald-600">{formatCurrency(summary.total_revenue)}</p>
                </div>
                <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">PayFast Revenue</p>
                  <p className="text-2xl font-bold text-blue-600">{formatCurrency(summary.payfast_revenue)}</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <CreditCard className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Invoice Revenue</p>
                  <p className="text-2xl font-bold text-indigo-600">{formatCurrency(summary.invoice_revenue)}</p>
                </div>
                <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
                  <FileText className="w-6 h-6 text-indigo-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Outstanding</p>
                  <p className="text-2xl font-bold text-amber-600">{formatCurrency(summary.total_outstanding)}</p>
                </div>
                <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                  <Clock className="w-6 h-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Commission Summary */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
          <Card className="border-purple-200 bg-purple-50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-purple-600">Commissions Due</p>
                  <p className="text-2xl font-bold text-purple-700">{formatCurrency(summary.commissions_due)}</p>
                  <p className="text-xs text-purple-500">{commissionTotals.payable ? `${partnerCommissions.length} partners` : 'No payouts pending'}</p>
                </div>
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <HandCoins className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Commissions Paid</p>
                  <p className="text-2xl font-bold">{formatCurrency(summary.commissions_paid)}</p>
                </div>
                <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center">
                  <BadgeCheck className="w-6 h-6 text-slate-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Paid Orders</p>
                  <p className="text-2xl font-bold">{counts.paid_orders || 0}</p>
                  <p className="text-xs text-slate-400">{counts.paid_invoices || 0} paid invoices</p>
                </div>
                <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center">
                  <Receipt className="w-6 h-6 text-slate-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white border mb-6">
            <TabsTrigger value="overview" className="gap-2">
              <TrendingUp className="w-4 h-4" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="invoices" className="gap-2">
              <FileText className="w-4 h-4" />
              Invoices ({counts.total_invoices || 0})
            </TabsTrigger>
            <TabsTrigger value="orders" className="gap-2">
              <CreditCard className="w-4 h-4" />
              PayFast Orders ({counts.total_orders || 0})
            </TabsTrigger>
            <TabsTrigger value="commissions" className="gap-2">
              <HandCoins className="w-4 h-4" />
              Commissions
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Monthly Revenue Chart */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Monthly Revenue</CardTitle>
                  <CardDescription>PayFast vs Invoice payments over 6 months</CardDescription>
                </CardHeader>
                <CardContent>
                  {monthlyData.length > 0 ? (
                    <div className="space-y-3">
                      {monthlyData.map((month, idx) => (
                        <div key={idx} className="space-y-1">
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-600">{month.month}</span>
                            <span className="font-medium">{formatCurrency(month.total)}</span>
                          </div>
                          <div className="flex gap-1 h-4">
                            <div 
                              className="bg-blue-500 rounded-l transition-all"
                              style={{ width: `${month.total > 0 ? (month.payfast / month.total * 100) : 0}%` }}
                              title={`PayFast: ${formatCurrency(month.payfast)}`}
                            />
                            <div 
                              className="bg-indigo-500 rounded-r transition-all"
                              style={{ width: `${month.total > 0 ? (month.invoice / month.total * 100) : 0}%` }}
                              title={`Invoice: ${formatCurrency(month.invoice)}`}
                            />
                          </div>
                        </div>
                      ))}
                      <div className="flex gap-4 pt-2 text-xs">
                        <div className="flex items-center gap-1">
                          <div className="w-3 h-3 bg-blue-500 rounded" />
                          <span>PayFast</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <div className="w-3 h-3 bg-indigo-500 rounded" />
                          <span>Invoice</span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-slate-500">
                      No revenue data yet
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Quick Stats */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Payment Statistics</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                          <CreditCard className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-medium">PayFast Orders</p>
                          <p className="text-sm text-slate-500">Online payments</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-lg">{counts.total_orders || 0}</p>
                        <p className="text-sm text-emerald-600">{counts.paid_orders || 0} paid</p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                          <FileText className="w-5 h-5 text-indigo-600" />
                        </div>
                        <div>
                          <p className="font-medium">Invoices</p>
                          <p className="text-sm text-slate-500">Manual payments</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-lg">{counts.total_invoices || 0}</p>
                        <p className="text-sm text-emerald-600">{counts.paid_invoices || 0} paid</p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                          <Clock className="w-5 h-5 text-amber-600" />
                        </div>
                        <div>
                          <p className="font-medium">Pending</p>
                          <p className="text-sm text-slate-500">Awaiting payment</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-lg">{counts.pending_invoices || 0}</p>
                        <p className="text-sm text-amber-600">{formatCurrency(summary.outstanding_invoices)}</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Invoices Tab */}
          <TabsContent value="invoices">
            <Card>
              <CardHeader className="flex-row items-center justify-between space-y-0 pb-4">
                <CardTitle>All Invoices</CardTitle>
                <div className="relative w-64">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input 
                    placeholder="Search invoices..." 
                    className="pl-9"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              </CardHeader>
              <CardContent>
                {recentInvoices.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>No invoices yet</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Invoice #</TableHead>
                        <TableHead>Buildings</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Due Date</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {recentInvoices
                        .filter(inv => 
                          inv.invoice_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          inv.accounts_manager_email?.toLowerCase().includes(searchQuery.toLowerCase())
                        )
                        .map(invoice => (
                          <TableRow key={invoice.invoice_id}>
                            <TableCell className="font-medium">{invoice.invoice_number}</TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Building2 className="w-4 h-4 text-slate-400" />
                                {invoice.buildings?.length || 0} building(s)
                              </div>
                            </TableCell>
                            <TableCell className="font-medium">{formatCurrency(invoice.total_amount)}</TableCell>
                            <TableCell>{getStatusBadge(invoice.status)}</TableCell>
                            <TableCell>{formatDate(invoice.due_date)}</TableCell>
                            <TableCell>
                              {invoice.status === 'pending' && (
                                <Button 
                                  size="sm" 
                                  onClick={() => {
                                    setSelectedInvoice(invoice);
                                    setShowMarkPaidDialog(true);
                                  }}
                                >
                                  Mark Paid
                                </Button>
                              )}
                              {invoice.status === 'paid' && (
                                <span className="text-sm text-emerald-600 flex items-center gap-1">
                                  <CheckCircle2 className="w-4 h-4" />
                                  Paid
                                </span>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* PayFast Orders Tab */}
          <TabsContent value="orders">
            <Card>
              <CardHeader className="flex-row items-center justify-between space-y-0 pb-4">
                <CardTitle>PayFast Orders</CardTitle>
              </CardHeader>
              <CardContent>
                {recentOrders.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <CreditCard className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>No PayFast orders yet</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Order ID</TableHead>
                        <TableHead>User</TableHead>
                        <TableHead>Items</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Date</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {recentOrders.map(order => (
                        <TableRow key={order.order_id}>
                          <TableCell className="font-mono text-sm">{order.order_id}</TableCell>
                          <TableCell>{order.user_email}</TableCell>
                          <TableCell>{order.items?.length || 0} building(s)</TableCell>
                          <TableCell className="font-medium">{formatCurrency(order.total_amount)}</TableCell>
                          <TableCell>{getStatusBadge(order.status)}</TableCell>
                          <TableCell>{formatDate(order.created_at)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Commissions Tab */}
          <TabsContent value="commissions">
            <div className="space-y-6">
              {/* Commission Summary Cards */}
              <div className="grid grid-cols-3 gap-4">
                <Card className="bg-amber-50 border-amber-200">
                  <CardContent className="p-4">
                    <p className="text-sm text-amber-600">Pending</p>
                    <p className="text-2xl font-bold text-amber-700">{formatCurrency(commissionTotals.pending)}</p>
                  </CardContent>
                </Card>
                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="p-4">
                    <p className="text-sm text-blue-600">Payable</p>
                    <p className="text-2xl font-bold text-blue-700">{formatCurrency(commissionTotals.payable)}</p>
                  </CardContent>
                </Card>
                <Card className="bg-emerald-50 border-emerald-200">
                  <CardContent className="p-4">
                    <p className="text-sm text-emerald-600">Paid</p>
                    <p className="text-2xl font-bold text-emerald-700">{formatCurrency(commissionTotals.paid)}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Partner Commission Table */}
              <Card>
                <CardHeader className="flex-row items-center justify-between space-y-0 pb-4">
                  <CardTitle>Partner Commissions</CardTitle>
                </CardHeader>
                <CardContent>
                  {partnerCommissions.length === 0 ? (
                    <div className="text-center py-12 text-slate-500">
                      <HandCoins className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                      <p>No commission records yet</p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Partner Code</TableHead>
                          <TableHead>Transactions</TableHead>
                          <TableHead>Total Revenue</TableHead>
                          <TableHead>Commission (10%)</TableHead>
                          <TableHead>Payable</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {partnerCommissions.map(partner => (
                          <TableRow key={partner.partner_code}>
                            <TableCell className="font-medium">{partner.partner_code}</TableCell>
                            <TableCell>{partner.total_transactions}</TableCell>
                            <TableCell>{formatCurrency(partner.total_amount)}</TableCell>
                            <TableCell className="font-medium">{formatCurrency(partner.commission_amount)}</TableCell>
                            <TableCell>
                              <Badge className={partner.payable > 0 ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'}>
                                {formatCurrency(partner.payable)}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {partner.payable > 0 && (
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => {
                                    setSelectedPartner(partner);
                                    setPayoutAmount(partner.payable.toString());
                                    setShowPayoutDialog(true);
                                  }}
                                >
                                  Pay Out
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Mark Invoice Paid Dialog */}
      <Dialog open={showMarkPaidDialog} onOpenChange={setShowMarkPaidDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              Mark Invoice as Paid
            </DialogTitle>
            <DialogDescription>
              Confirm payment for invoice {selectedInvoice?.invoice_number}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="p-3 bg-slate-50 rounded-lg">
              <div className="flex justify-between mb-2">
                <span className="text-slate-600">Amount</span>
                <span className="font-bold text-lg">{formatCurrency(selectedInvoice?.total_amount)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Buildings</span>
                <span>{selectedInvoice?.buildings?.length || 0} building(s)</span>
              </div>
            </div>

            <div>
              <Label>Payment Reference *</Label>
              <Input
                value={paymentReference}
                onChange={(e) => setPaymentReference(e.target.value)}
                placeholder="Bank reference or receipt number"
                className="mt-1"
              />
            </div>

            <div>
              <Label>Payment Date (Optional)</Label>
              <Input
                type="date"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
                className="mt-1"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMarkPaidDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleMarkPaid} disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                <>
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Confirm Payment
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Commission Payout Dialog */}
      <Dialog open={showPayoutDialog} onOpenChange={setShowPayoutDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <HandCoins className="w-5 h-5 text-purple-600" />
              Process Commission Payout
            </DialogTitle>
            <DialogDescription>
              Pay commission to partner {selectedPartner?.partner_code}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="p-3 bg-purple-50 rounded-lg">
              <div className="flex justify-between mb-2">
                <span className="text-purple-600">Payable Amount</span>
                <span className="font-bold text-lg text-purple-700">{formatCurrency(selectedPartner?.payable)}</span>
              </div>
            </div>

            <div>
              <Label>Payout Amount *</Label>
              <Input
                type="number"
                value={payoutAmount}
                onChange={(e) => setPayoutAmount(e.target.value)}
                placeholder="Enter amount"
                className="mt-1"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Period Start *</Label>
                <Input
                  type="date"
                  value={payoutPeriodStart}
                  onChange={(e) => setPayoutPeriodStart(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Period End *</Label>
                <Input
                  type="date"
                  value={payoutPeriodEnd}
                  onChange={(e) => setPayoutPeriodEnd(e.target.value)}
                  className="mt-1"
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPayoutDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCommissionPayout} disabled={submitting} className="bg-purple-600 hover:bg-purple-700">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                <>
                  <HandCoins className="w-4 h-4 mr-2" />
                  Process Payout
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
